/**
 * QueueTable Component
 * 
 * Paginated table showing the global scrape queue with job/adapter/suburb relations.
 */

import { useState } from "react";
import { 
  ChevronLeft, 
  ChevronRight, 
  ExternalLink, 
  Loader2, 
  AlertCircle, 
  Clock, 
  CheckCircle2, 
  CircleDot,
} from "lucide-react";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui/table";
import { cn } from "@repo/ui/lib/utils";
import type { QueueItemWithRelations } from "@/lib/api/queue";

interface QueueTableProps {
  items: QueueItemWithRelations[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onRefresh: () => void;
}

const statusConfig = {
  pending: { 
    label: "Pending", 
    icon: Clock, 
    className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400" 
  },
  in_progress: { 
    label: "In Progress", 
    icon: CircleDot, 
    className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400" 
  },
  completed: { 
    label: "Completed", 
    icon: CheckCircle2, 
    className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" 
  },
  failed: { 
    label: "Failed", 
    icon: AlertCircle, 
    className: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400" 
  },
};

function truncateUrl(url: string, maxLength = 60): string {
  if (url.length <= maxLength) return url;
  // Show domain + start of path + end
  try {
    const parsed = new URL(url);
    const path = parsed.pathname + parsed.search;
    if (path.length > maxLength - parsed.host.length - 10) {
      return `${parsed.host}${path.slice(0, 30)}...${path.slice(-15)}`;
    }
    return `${url.slice(0, maxLength - 3)}...`;
  } catch {
    return `${url.slice(0, maxLength - 3)}...`;
  }
}

function formatTimeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export function QueueTable({
  items,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  isLoading,
  onPageChange,
  onRefresh: _onRefresh,
}: QueueTableProps) {
  const [expandedError, setExpandedError] = useState<string | null>(null);

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[180px]">Job</TableHead>
            <TableHead className="w-[120px]">Adapter</TableHead>
            <TableHead className="w-[140px]">Suburb</TableHead>
            <TableHead>URL</TableHead>
            <TableHead className="w-[110px]">Status</TableHead>
            <TableHead className="w-[70px] text-center">Tries</TableHead>
            <TableHead className="w-[100px] text-right">Updated</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-12">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground mt-2">Loading queue...</p>
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                <div className="space-y-2">
                  <Clock className="w-8 h-8 mx-auto opacity-50" />
                  <p>No queue items found</p>
                  <p className="text-sm">Run a scrape job to populate the queue</p>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => {
              const config = statusConfig[item.status];
              const StatusIcon = config.icon;

              return (
                <TableRow key={item.id} className="group">
                  <TableCell>
                    <span className="font-medium text-sm truncate block max-w-[170px]">
                      {item.scrape_jobs?.name || "Unknown Job"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs font-normal">
                      {item.adapters?.name || "Unknown"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {item.suburbs ? (
                      <div className="text-sm">
                        <span className="font-medium">{item.suburbs.suburb_name}</span>
                        <span className="text-muted-foreground ml-1 text-xs">
                          {item.suburbs.state} {item.suburbs.postcode}
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-sm font-mono text-muted-foreground truncate block max-w-[350px]">
                            {truncateUrl(item.url)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-md break-all">
                          {item.url}
                        </TooltipContent>
                      </Tooltip>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      >
                        <ExternalLink className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                      </a>
                    </div>
                    {item.status === 'failed' && item.last_error && (
                      <button
                        type="button"
                        onClick={() => setExpandedError(expandedError === item.id ? null : item.id)}
                        className="text-xs text-red-500 hover:text-red-400 mt-0.5 text-left"
                      >
                        {expandedError === item.id ? item.last_error : (
                          item.last_error.length > 60 
                            ? `${item.last_error.slice(0, 60)}...` 
                            : item.last_error
                        )}
                      </button>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={cn("gap-1 text-xs", config.className)}>
                      <StatusIcon className="w-3 h-3" />
                      {config.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={cn(
                      "text-sm font-mono",
                      item.attempts > 1 && "text-amber-600 dark:text-amber-400",
                      item.attempts >= 3 && "text-red-600 dark:text-red-400"
                    )}>
                      {item.attempts}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="text-xs text-muted-foreground">
                      {formatTimeAgo(item.updated_at)}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * pageSize) + 1} to{" "}
            {Math.min(currentPage * pageSize, totalCount)} of{" "}
            {totalCount.toLocaleString()} items
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1 || isLoading}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages || isLoading}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
